import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  Calendar,
  CheckCircle2,
  Download,
  RefreshCw,
  Activity,
  Zap,
  Users,
  Server,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from 'recharts';
import {
  fetchDailyObservabilityMetrics,
  type DailyApiMetric,
  type DailyErrorMetric,
} from '../../services/MasterAdmin/observabilityService';
import InsightsPageShell from '../../components/MasterAdmin/insights/InsightsPageShell';
import InsightsStatCard from '../../components/MasterAdmin/insights/InsightsStatCard';
import InsightsChartCard, { INSIGHTS_CHART_TOOLTIP } from '../../components/MasterAdmin/insights/InsightsChartCard';
import InsightsSectionHeader from '../../components/MasterAdmin/insights/InsightsSectionHeader';
import {
  opsFilterClass,
  opsPrimaryBtn,
  opsSecondaryBtn,
  opsTableWrap,
  opsMetricGridClass,
  CHART_COLORS,
  PIE_PALETTE,
} from '../../components/MasterAdmin/operations/operationsUi';
import { reportsMetricInfo } from '../../config/masterAdminMetricDefinitions';

function addDays(base: Date, days: number): Date {
  return new Date(base.getTime() + days * 24 * 60 * 60 * 1000);
}

function dateValue(date: Date | null): string {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseDate(value: string): Date | null {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatTrendLabel(label: string): string {
  if (!label) return '';
  const parts = label.split('-');
  if (parts.length !== 3) return label;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthIdx = parseInt(parts[1], 10) - 1;
  return `${months[monthIdx]} ${parts[2]}, ${parts[0]}`;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const requests = payload[0]?.value || 0;
  const latency = payload[1]?.value || 0;

  let insight = 'Healthy: performance and resource allocation are scaling well.';
  let statusColor = 'text-emerald-400';
  if (latency > 800) {
    insight = 'Critical: high latency bottleneck. Check third-party API logs or DB pooling.';
    statusColor = 'text-red-400';
  } else if (latency > 400) {
    insight = 'Warning: moderate latency elevation. Monitor slow queries.';
    statusColor = 'text-amber-400';
  } else if (requests > 50 && latency < 200) {
    insight = 'Optimal: low latency under moderate traffic.';
    statusColor = 'text-emerald-400';
  }

  return (
    <div style={INSIGHTS_CHART_TOOLTIP} className="p-3 max-w-[260px] text-left leading-relaxed">
      <p className="mb-2 font-semibold text-gray-300">{formatTrendLabel(label)}</p>
      <div className="mb-1 flex justify-between gap-4">
        <span className="text-gray-400">Traffic</span>
        <span className="font-semibold text-blue-400">{requests.toLocaleString()} req</span>
      </div>
      <div className="mb-2 flex justify-between gap-4">
        <span className="text-gray-400">Avg latency</span>
        <span className="font-semibold text-purple-400">{Math.round(latency)} ms</span>
      </div>
      <p className="border-t border-gray-700 pt-2 text-[11px] text-gray-300">
        <strong className={statusColor}>Insight: </strong>
        {insight}
      </p>
    </div>
  );
};

const ReportsPage: React.FC = () => {
  const [fromDate, setFromDate] = useState<Date | null>(addDays(new Date(), -7));
  const [toDate, setToDate] = useState<Date | null>(new Date());
  const [apiRows, setApiRows] = useState<DailyApiMetric[]>([]);
  const [errorRows, setErrorRows] = useState<DailyErrorMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!fromDate || !toDate || toDate < fromDate) return;
    try {
      setLoading(true);
      setError(null);
      const res = await fetchDailyObservabilityMetrics({
        from: dateValue(fromDate),
        to: dateValue(toDate),
        limit: 500,
      });
      setApiRows(res.data?.apiRows || []);
      setErrorRows(res.data?.errorRows || []);
    } catch (e) {
      console.error('Failed to load observability reports', e);
      setError('Failed to load observability reports');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const kpis = useMemo(() => {
    const totalRequests = apiRows.reduce((sum, row) => sum + Number(row.total_requests || 0), 0);
    const weightedErrors = apiRows.reduce(
      (sum, row) => sum + Number(row.total_requests || 0) * Number(row.error_rate || 0),
      0
    );
    const avgLatencyWeighted = apiRows.reduce(
      (sum, row) => sum + Number(row.total_requests || 0) * Number(row.avg_response_time || 0),
      0
    );
    const p95LatencyWeighted = apiRows.reduce(
      (sum, row) =>
        sum + Number(row.total_requests || 0) * Number(row.p95_response_time || row.avg_response_time || 0),
      0
    );
    const totalErrorOccurrences = errorRows.reduce((sum, row) => sum + Number(row.total_occurrences || 0), 0);
    const uniqueUsersAffected = errorRows.reduce((sum, row) => sum + Number(row.unique_users || 0), 0);

    const overallErrorRate = totalRequests > 0 ? (weightedErrors / totalRequests) * 100 : 0;
    const avgLatencyMs = totalRequests > 0 ? avgLatencyWeighted / totalRequests : 0;
    const p95LatencyMs = totalRequests > 0 ? p95LatencyWeighted / totalRequests : 0;
    const errorStatus =
      overallErrorRate > 2 ? 'CRITICAL' : overallErrorRate > 0.5 ? 'WARNING' : 'HEALTHY';

    return {
      totalRequests,
      overallErrorRate,
      avgLatencyMs,
      p95LatencyMs,
      totalErrorOccurrences,
      uniqueUsersAffected,
      errorStatus,
    };
  }, [apiRows, errorRows]);

  const trendData = useMemo(() => {
    const daily = new Map<
      string,
      { date: string; requests: number; latency: number; errorRate: number; count: number }
    >();
    apiRows.forEach((row) => {
      const dateRaw = row.date;
      const date =
        dateRaw && typeof dateRaw === 'object' && 'value' in dateRaw
          ? String((dateRaw as any).value)
          : String(dateRaw || '');

      const existing = daily.get(date) || { date, requests: 0, latency: 0, errorRate: 0, count: 0 };
      const reqs = Number(row.total_requests || 0);
      existing.requests += reqs;
      existing.latency += reqs * Number(row.avg_response_time || 0);
      existing.errorRate += reqs * Number(row.error_rate || 0);
      existing.count += reqs;
      daily.set(date, existing);
    });
    return Array.from(daily.values())
      .sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')))
      .map((d) => ({
        ...d,
        latency: d.count > 0 ? d.latency / d.count : 0,
        errorRate: d.count > 0 ? (d.errorRate / d.count) * 100 : 0,
      }));
  }, [apiRows]);

  const topEndpoints = useMemo(() => {
    const grouped = new Map<
      string,
      { endpoint: string; requests: number; errors: number; avgLatencySum: number; p95LatencySum: number }
    >();
    apiRows.forEach((row) => {
      const key = row.endpoint;
      const requests = Number(row.total_requests || 0);
      const errors = requests * Number(row.error_rate || 0);
      const prev = grouped.get(key) || {
        endpoint: key,
        requests: 0,
        errors: 0,
        avgLatencySum: 0,
        p95LatencySum: 0,
      };
      prev.requests += requests;
      prev.errors += errors;
      prev.avgLatencySum += requests * Number(row.avg_response_time || 0);
      prev.p95LatencySum += requests * Number(row.p95_response_time || row.avg_response_time || 0);
      grouped.set(key, prev);
    });
    return Array.from(grouped.values())
      .sort((a, b) => b.requests - a.requests)
      .slice(0, 10)
      .map((item) => ({
        endpoint: item.endpoint,
        requests: item.requests,
        errorRate: item.requests > 0 ? (item.errors / item.requests) * 100 : 0,
        avgLatency: item.requests > 0 ? item.avgLatencySum / item.requests : 0,
        p95Latency: item.requests > 0 ? item.p95LatencySum / item.requests : 0,
      }));
  }, [apiRows]);

  const topErrorTypes = useMemo(() => {
    const grouped = new Map<string, { errorType: string; count: number; users: number }>();
    errorRows.forEach((row) => {
      const key = row.error_type || 'UNKNOWN';
      const prev = grouped.get(key) || { errorType: key, count: 0, users: 0 };
      prev.count += Number(row.total_occurrences || 0);
      prev.users += Number(row.unique_users || 0);
      grouped.set(key, prev);
    });
    return Array.from(grouped.values()).sort((a, b) => b.count - a.count).slice(0, 10);
  }, [errorRows]);

  const totalUniqueEndpoints = useMemo(() => {
    return new Set(apiRows.map((r) => r.endpoint).filter(Boolean)).size;
  }, [apiRows]);

  const totalUniqueErrors = useMemo(() => {
    const nonNullErrors = errorRows.filter((r) => r.error_type && Number(r.total_occurrences || 0) > 0);
    return new Set(nonNullErrors.map((r) => r.error_type)).size;
  }, [errorRows]);

  const exportToCSV = () => {
    const headers = ['Endpoint', 'Requests', 'Error Rate (%)', 'Avg Latency (ms)', 'P95 Latency (ms)'];
    const rows = topEndpoints.map((e) => [
      e.endpoint,
      e.requests,
      e.errorRate.toFixed(2),
      e.avgLatency.toFixed(0),
      e.p95Latency.toFixed(0),
    ]);
    const csvContent = [headers, ...rows].map((e) => e.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `api_performance_${dateValue(fromDate)}_to_${dateValue(toDate)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const errorAccent =
    kpis.errorStatus === 'CRITICAL'
      ? 'text-red-500'
      : kpis.errorStatus === 'WARNING'
        ? 'text-amber-500'
        : 'text-emerald-500';

  return (
    <InsightsPageShell
      title="API & Error Report"
      description="Traffic, latency, and exception health for production APIs."
      info={reportsMetricInfo.page}
      actions={
        <>
          <label className={`${opsFilterClass} flex items-center gap-2`}>
            <Calendar className="h-4 w-4 shrink-0 text-gray-400" />
            <input
              type="date"
              value={dateValue(fromDate)}
              onChange={(e) => setFromDate(parseDate(e.currentTarget.value))}
              className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none dark:text-white"
            />
          </label>
          <label className={`${opsFilterClass} flex items-center gap-2`}>
            <Calendar className="h-4 w-4 shrink-0 text-gray-400" />
            <input
              type="date"
              value={dateValue(toDate)}
              onChange={(e) => setToDate(parseDate(e.currentTarget.value))}
              className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none dark:text-white"
            />
          </label>
          <button type="button" onClick={() => void loadData()} disabled={loading} className={opsPrimaryBtn}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </>
      }
    >
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      <div className={opsMetricGridClass}>
        <InsightsStatCard
          label="Total API Calls"
          value={kpis.totalRequests.toLocaleString()}
          icon={Server}
          loading={loading}
          info={reportsMetricInfo.totalApiCalls}
          sub="Auto-scaling enabled"
        />
        <InsightsStatCard
          label="Overall Error Rate"
          value={`${kpis.overallErrorRate.toFixed(2)}%`}
          icon={AlertTriangle}
          accent={errorAccent}
          loading={loading}
          info={reportsMetricInfo.overallErrorRate}
          sub={kpis.errorStatus}
          className={
            kpis.errorStatus === 'CRITICAL'
              ? 'border-red-200 bg-red-50/50 dark:border-red-900/40 dark:bg-red-900/10'
              : kpis.errorStatus === 'WARNING'
                ? 'border-amber-200 bg-amber-50/50 dark:border-amber-900/40 dark:bg-amber-900/10'
                : ''
          }
        />
        <InsightsStatCard
          label="Avg Response Time"
          value={`${kpis.avgLatencyMs.toFixed(0)} ms`}
          icon={Zap}
          accent="text-amber-500"
          loading={loading}
          info={reportsMetricInfo.avgResponseTime}
          sub="Target < 200ms"
        />
        <InsightsStatCard
          label="P95 Response Time"
          value={`${kpis.p95LatencyMs.toFixed(0)} ms`}
          icon={Activity}
          accent={
            kpis.p95LatencyMs > 800
              ? 'text-red-500'
              : kpis.p95LatencyMs > 400
                ? 'text-amber-500'
                : 'text-purple-500'
          }
          loading={loading}
          info={reportsMetricInfo.p95ResponseTime}
          sub="SLA < 500ms"
        />
        <InsightsStatCard
          label="Total Exceptions"
          value={kpis.totalErrorOccurrences.toLocaleString()}
          icon={AlertTriangle}
          accent="text-red-500"
          loading={loading}
          info={reportsMetricInfo.totalExceptions}
          sub="Goal: 0"
        />
        <InsightsStatCard
          label="Users Impacted"
          value={kpis.uniqueUsersAffected.toLocaleString()}
          icon={Users}
          accent="text-orange-500"
          loading={loading}
          info={reportsMetricInfo.usersImpacted}
          sub="Goal: 0"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <InsightsChartCard
          title="Performance Trends"
          info={reportsMetricInfo.performanceTrends}
          className="xl:col-span-2"
          heightClass="h-64"
          actions={
            <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              <span className="flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-blue-500" /> Requests
              </span>
              <span className="flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-purple-500" /> Latency
              </span>
            </div>
          }
        >
          {loading ? (
            <div className="flex h-full items-center justify-center">
              <RefreshCw className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          ) : trendData.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-500">No trend data</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <defs>
                  <linearGradient id="reportsColorReq" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="opacity-20" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(tick) => {
                    if (!tick) return '';
                    const parts = String(tick).split('-');
                    return parts.length === 3 ? `${parts[1]}/${parts[2]}` : tick;
                  }}
                  stroke="#94a3b8"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis yAxisId="left" hide />
                <YAxis yAxisId="right" orientation="right" hide />
                <RechartsTooltip content={<CustomTooltip />} />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="requests"
                  stroke={CHART_COLORS.primary}
                  fill="url(#reportsColorReq)"
                  name="Requests"
                  strokeWidth={2}
                />
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="latency"
                  stroke={CHART_COLORS.purple}
                  fill="none"
                  name="Latency (ms)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </InsightsChartCard>

        <InsightsChartCard title="Top endpoints by volume" heightClass="h-64">
          {loading ? (
            <div className="flex h-full items-center justify-center">
              <RefreshCw className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          ) : topEndpoints.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-500">No endpoint data</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={topEndpoints.slice(0, 6).map((e) => ({
                  name: e.endpoint.length > 18 ? `${e.endpoint.slice(0, 18)}…` : e.endpoint,
                  full: e.endpoint,
                  requests: e.requests,
                }))}
                layout="vertical"
                margin={{ top: 4, right: 8, left: 4, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" className="opacity-20" horizontal={false} />
                <XAxis type="number" hide />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={110}
                  tick={{ fontSize: 10, fill: '#94a3b8' }}
                  tickLine={false}
                  axisLine={false}
                />
                <RechartsTooltip
                  contentStyle={INSIGHTS_CHART_TOOLTIP}
                  formatter={(value: number) => [value.toLocaleString(), 'Requests']}
                  labelFormatter={(_, payload) => payload?.[0]?.payload?.full || ''}
                />
                <Bar dataKey="requests" radius={[0, 6, 6, 0]} maxBarSize={22}>
                  {topEndpoints.slice(0, 6).map((_, i) => (
                    <Cell key={i} fill={PIE_PALETTE[i % PIE_PALETTE.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </InsightsChartCard>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-5">
          <InsightsSectionHeader
            title="Top API Endpoints"
            info={reportsMetricInfo.topEndpoints}
            actions={
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  {totalUniqueEndpoints} total
                </span>
                <button type="button" onClick={exportToCSV} className={opsSecondaryBtn}>
                  <Download className="h-3.5 w-3.5" />
                  CSV
                </button>
              </div>
            }
          />
          <div className={`${opsTableWrap} max-h-[420px]`}>
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:bg-gray-800/80 dark:text-gray-400">
                <tr>
                  <th className="px-4 py-2">Endpoint</th>
                  <th className="px-4 py-2">Requests</th>
                  <th className="px-4 py-2">Error %</th>
                  <th className="px-4 py-2">Avg</th>
                  <th className="px-4 py-2 text-purple-600 dark:text-purple-400">P95</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-500">
                      <RefreshCw className="mx-auto mb-2 h-6 w-6 animate-spin text-primary-600" />
                      Loading endpoints…
                    </td>
                  </tr>
                ) : topEndpoints.length > 0 ? (
                  topEndpoints.map((row) => (
                    <tr key={row.endpoint} className="border-t border-gray-100 dark:border-gray-700/60">
                      <td
                        className="max-w-[240px] truncate px-4 py-2.5 font-medium text-gray-800 dark:text-gray-200"
                        title={row.endpoint}
                      >
                        {row.endpoint}
                      </td>
                      <td className="px-4 py-2.5 tabular-nums text-gray-700 dark:text-gray-300">
                        {row.requests.toLocaleString()}
                      </td>
                      <td className="px-4 py-2.5 tabular-nums">
                        <span className={row.errorRate > 5 ? 'font-semibold text-red-500' : 'text-gray-700 dark:text-gray-300'}>
                          {row.errorRate.toFixed(2)}%
                        </span>
                      </td>
                      <td className="px-4 py-2.5 tabular-nums text-gray-700 dark:text-gray-300">
                        {row.avgLatency.toFixed(0)} ms
                      </td>
                      <td className="px-4 py-2.5 font-semibold tabular-nums text-purple-600 dark:text-purple-400">
                        {row.p95Latency.toFixed(0)} ms
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-500">
                      No API traffic tracked in this date range.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-5">
          <InsightsSectionHeader
            title="Top Error Types"
            info={reportsMetricInfo.topErrorTypes}
            actions={
              totalUniqueErrors > 0 ? (
                <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-600 dark:bg-red-900/30 dark:text-red-400">
                  {totalUniqueErrors} total
                </span>
              ) : undefined
            }
          />
          <div className={`${opsTableWrap} max-h-[420px]`}>
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:bg-gray-800/80 dark:text-gray-400">
                <tr>
                  <th className="px-4 py-2">
                    <span className="inline-flex items-center gap-1.5">
                      <BarChart3 className="h-3.5 w-3.5" /> Error Type
                    </span>
                  </th>
                  <th className="px-4 py-2">Occurrences</th>
                  <th className="px-4 py-2">Users</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-12 text-center text-sm text-gray-500">
                      <RefreshCw className="mx-auto mb-2 h-6 w-6 animate-spin text-primary-600" />
                      Loading errors…
                    </td>
                  </tr>
                ) : topErrorTypes.length > 0 ? (
                  topErrorTypes.map((row) => (
                    <tr key={row.errorType} className="border-t border-gray-100 dark:border-gray-700/60">
                      <td className="px-4 py-2.5 font-medium text-gray-800 dark:text-gray-200">{row.errorType}</td>
                      <td className="px-4 py-2.5 tabular-nums text-gray-700 dark:text-gray-300">
                        {row.count.toLocaleString()}
                      </td>
                      <td className="px-4 py-2.5 tabular-nums text-gray-700 dark:text-gray-300">
                        {row.users.toLocaleString()}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <div className="rounded-full bg-emerald-100 p-2 dark:bg-emerald-900/30">
                          <CheckCircle2 className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">All systems stable</p>
                        <p className="max-w-[220px] text-xs text-gray-500">
                          No application exceptions logged in this window.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </InsightsPageShell>
  );
};

export default ReportsPage;
